/**
 * Error Handling Utilities
 *
 * Provides error mapping, logging, and retry logic.
 */

import * as Sentry from '@sentry/react';

// Firebase error code to user-friendly message mapping
const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/operation-not-allowed': 'This operation is not allowed.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-credential': 'Invalid email or password. Please try again.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/popup-closed-by-user': 'Sign-in popup was closed before completion.',
  'auth/requires-recent-login': 'Please sign in again to complete this action.',
  'auth/credential-already-in-use': 'This credential is already associated with another account.',

  // Firestore errors
  'permission-denied': 'You do not have permission to perform this action.',
  'not-found': 'The requested resource was not found.',
  'already-exists': 'This resource already exists.',
  'failed-precondition': 'Operation failed. Please try again.',
  'aborted': 'Operation was aborted. Please try again.',
  'out-of-range': 'Invalid range specified.',
  'unimplemented': 'This feature is not available.',
  'internal': 'An internal error occurred. Please try again.',
  'unavailable': 'Service is temporarily unavailable. Please try again.',
  'data-loss': 'Data loss occurred. Please contact support.',
  'unauthenticated': 'Please sign in to continue.',
  'resource-exhausted': 'Too many requests. Please try again later.',
  'cancelled': 'Operation was cancelled.',
  'deadline-exceeded': 'Request timed out. Please try again.',

  // Storage errors
  'storage/unknown': 'An unknown error occurred with file storage.',
  'storage/object-not-found': 'File not found.',
  'storage/bucket-not-found': 'Storage bucket not found.',
  'storage/project-not-found': 'Storage project not found.',
  'storage/quota-exceeded': 'Storage quota exceeded.',
  'storage/unauthenticated': 'Please sign in to upload files.',
  'storage/unauthorized': 'You do not have permission to access this file.',
  'storage/retry-limit-exceeded': 'Upload failed. Please try again.',
  'storage/invalid-checksum': 'File corrupted during upload. Please try again.',
  'storage/canceled': 'Upload was cancelled.',
  'storage/invalid-url': 'Invalid storage URL.',
  'storage/cannot-slice-blob': 'File cannot be processed.',
  'storage/server-file-wrong-size': 'File size mismatch. Please try again.',
};

/**
 * Get user-friendly error message from Firebase error code
 */
export function getFirebaseErrorMessage(code: string): string {
  return FIREBASE_ERROR_MESSAGES[code] || 'An unexpected error occurred. Please try again.';
}

/**
 * Application error class
 */
export class AppError extends Error {
  constructor(
    message: string,
    public code: string = 'unknown',
    public statusCode: number = 500,
    public isOperational: boolean = true
  ) {
    super(message);
    this.name = 'AppError';
    Object.setPrototypeOf(this, AppError.prototype);
  }
}

/**
 * Parse error into standardized format
 */
export function parseError(error: unknown): { code: string; message: string; original: unknown } {
  if (error instanceof AppError) {
    return {
      code: error.code,
      message: error.message,
      original: error,
    };
  }

  if (error instanceof Error) {
    const firebaseError = error as Error & { code?: string };
    const code = firebaseError.code || 'unknown';
    return {
      code,
      message: getFirebaseErrorMessage(code) || error.message,
      original: error,
    };
  }

  if (typeof error === 'string') {
    return {
      code: 'unknown',
      message: error,
      original: error,
    };
  }

  return {
    code: 'unknown',
    message: 'An unexpected error occurred.',
    original: error,
  };
}

/**
 * Log error to console (and optionally to external service)
 */
export function logError(
  error: unknown,
  context?: Record<string, unknown>
): void {
  const parsed = parseError(error);

  console.error('Application Error:', {
    code: parsed.code,
    message: parsed.message,
    context,
    timestamp: new Date().toISOString(),
    original: parsed.original,
  });

  // Safe no-op when Sentry.init was never called (no DSN configured).
  Sentry.captureException(
    parsed.original instanceof Error ? parsed.original : new Error(parsed.message),
    { extra: { code: parsed.code, ...context } }
  );
}

/**
 * Check if error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  const parsed = parseError(error);
  return (
    parsed.code === 'auth/network-request-failed' ||
    parsed.code === 'unavailable' ||
    parsed.message.toLowerCase().includes('network')
  );
}

/**
 * Check if error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  const parsed = parseError(error);
  return (
    parsed.code.startsWith('auth/') ||
    parsed.code === 'unauthenticated' ||
    parsed.code === 'permission-denied'
  );
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  const parsed = parseError(error);
  const retryableCodes = [
    'unavailable',
    'deadline-exceeded',
    'aborted',
    'internal',
    'auth/network-request-failed',
    'storage/retry-limit-exceeded',
    'resource-exhausted',
  ];
  return retryableCodes.includes(parsed.code) || isNetworkError(error);
}

/**
 * Retry options for async operations
 */
export interface RetryOptions {
  maxAttempts?: number;
  delayMs?: number;
  backoffMultiplier?: number;
  maxDelayMs?: number;
  shouldRetry?: (error: unknown, attempt: number) => boolean;
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  delayMs: 1000,
  backoffMultiplier: 2,
  maxDelayMs: 10000,
  shouldRetry: (error) => isRetryableError(error),
};

/**
 * Retry an async operation with exponential backoff
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: unknown;
  let delay = config.delayMs;

  for (let attempt = 1; attempt <= config.maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (attempt === config.maxAttempts || !config.shouldRetry(error, attempt)) {
        throw error;
      }

      // Wait before retrying
      await sleep(delay);

      // Increase delay with backoff
      delay = Math.min(delay * config.backoffMultiplier, config.maxDelayMs);
    }
  }

  throw lastError;
}

/**
 * Sleep for a specified duration
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Create an error handler for async functions
 */
export function createErrorHandler(
  onError: (error: { code: string; message: string }) => void
): (error: unknown) => void {
  return (error: unknown) => {
    const parsed = parseError(error);
    logError(error);
    onError({ code: parsed.code, message: parsed.message });
  };
}

/**
 * Wrap an async function with error handling
 */
export function withErrorHandling<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  onError?: (error: { code: string; message: string }) => void
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      const parsed = parseError(error);
      logError(error);
      if (onError) {
        onError({ code: parsed.code, message: parsed.message });
      }
      throw error;
    }
  }) as T;
}
